'use client';

import { useEffect, useRef, useState } from 'react';

import { format, isValid, parse } from 'date-fns';
import { DayPicker } from 'react-day-picker';

type DatePickerProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  allowFutureDates?: boolean;
};

export default function DatePicker({
  id,
  value,
  onChange,
  onBlur,
  placeholder = 'dd/mm/yyyy',
  className = '',
  allowFutureDates = false,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(
    value ? format(new Date(value), 'dd/MM/yyyy') : '',
  );
  const [showYearMonth, setShowYearMonth] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(
    value ? new Date(value) : new Date(),
  );
  const ref = useRef<HTMLDivElement>(null);

  const selected = value ? new Date(value) : undefined;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setShowYearMonth(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sync input when value changes externally
  useEffect(() => {
    setInputValue(value ? format(new Date(value), 'dd/MM/yyyy') : '');
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 2) raw = raw.slice(0, 2) + '/' + raw.slice(2);
    if (raw.length > 5) raw = raw.slice(0, 5) + '/' + raw.slice(5);
    if (raw.length > 10) raw = raw.slice(0, 10);
    setInputValue(raw);

    if (raw.length === 10) {
      const parsed = parse(raw, 'dd/MM/yyyy', new Date());
      const [d, m, y] = raw.split('/').map(Number);
      const noRollover =
        parsed.getDate() === d &&
        parsed.getMonth() + 1 === m &&
        parsed.getFullYear() === y;
      if (
        isValid(parsed) &&
        noRollover &&
        (allowFutureDates || parsed <= new Date())
      ) {
        onChange(format(parsed, 'yyyy-MM-dd'));
        setCalendarMonth(parsed);
      }
    }
  };

  const handleYearSelect = (year: number) => {
    const updated = new Date(calendarMonth);
    updated.setFullYear(year);
    setCalendarMonth(updated);
    setShowYearMonth(false);
  };

  const handleMonthSelect = (monthIndex: number) => {
    const updated = new Date(calendarMonth);
    updated.setMonth(monthIndex);
    setCalendarMonth(updated);
    setShowYearMonth(false);
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
  };

  return (
    <div ref={ref} className="relative">
      <input
        id={id}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={10}
        className={`hover:border-royal-300 focus:ring-theme-primary-light h-9 w-full rounded-lg border border-gray-200 px-3 text-left text-xs font-normal transition-colors duration-200 outline-none hover:bg-gray-50/50 focus:-translate-y-px focus:ring-2 focus:ring-offset-2 focus:ring-offset-white ${
          inputValue ? 'pr-7 text-gray-600' : 'text-gray-400'
        } ${className}`}
      />
      {inputValue && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
          className="absolute top-1/2 right-2 flex size-4 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          aria-label="Clear date"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="size-3"
          >
            <path d="M5.28 4.22a.75.75 0 0 0-1.06 1.06L6.94 8l-2.72 2.72a.75.75 0 1 0 1.06 1.06L8 9.06l2.72 2.72a.75.75 0 1 0 1.06-1.06L9.06 8l2.72-2.72a.75.75 0 0 0-1.06-1.06L8 6.94 5.28 4.22Z" />
          </svg>
        </button>
      )}

      {open && (
        <div
          className="absolute z-50 mt-1 rounded-lg border border-gray-100 bg-white p-3 shadow-lg"
          onMouseDown={(e) => e.preventDefault()}
        >
          {showYearMonth ? (
            <div className="w-[232px]">
              {/* Month grid */}
              <div className="mb-3 grid grid-cols-3 gap-1">
                {months.map((month, i) => (
                  <button
                    key={month}
                    type="button"
                    onClick={() => handleMonthSelect(i)}
                    className={`cursor-pointer rounded-md py-1.5 text-xs transition-colors ${
                      i === calendarMonth.getMonth()
                        ? 'bg-royal-500 font-semibold text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {month.slice(0, 3)}
                  </button>
                ))}
              </div>
              {/* Year list */}
              <div className="h-40 overflow-y-auto border-t border-gray-100 pt-2">
                {years.map((year) => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => handleYearSelect(year)}
                    className={`w-full cursor-pointer rounded-md px-2 py-1 text-left text-xs transition-colors ${
                      year === calendarMonth.getFullYear()
                        ? 'bg-royal-500 font-semibold text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <DayPicker
              mode="single"
              disabled={!allowFutureDates ? { after: new Date() } : undefined}
              selected={selected}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              onSelect={(date) => {
                if (date) {
                  onChange(format(date, 'yyyy-MM-dd')); // ← ISO for the prop
                  setInputValue(format(date, 'dd/MM/yyyy')); // ← display format for input
                  setOpen(false);
                }
              }}
              classNames={{
                months: 'relative',
                // eslint-disable-next-line camelcase
                month_caption:
                  'flex items-center justify-center font-semibold text-sm text-gray-800 h-9',
                nav: 'absolute inset-x-0 top-0 flex justify-between items-center h-9 px-1 pointer-events-none',
                // eslint-disable-next-line camelcase
                button_previous:
                  'flex items-center justify-center size-7 rounded-full hover:bg-gray-100 cursor-pointer transition-colors pointer-events-auto',
                // eslint-disable-next-line camelcase
                button_next:
                  'flex items-center justify-center size-7 rounded-full hover:bg-gray-100 cursor-pointer transition-colors pointer-events-auto',
                chevron: 'fill-royal-500 size-4',
                // eslint-disable-next-line camelcase
                day_button: `size-9 rounded-full text-xs font-normal text-gray-700 cursor-pointer transition-colors hover:bg-gray-100! aria-selected:bg-gray-100! aria-selected:hover:bg-royal-850!`,
                today: 'font-semibold text-royal-500',
                weekdays: 'grid grid-cols-7 mt-2',
                weekday:
                  'size-9 flex items-center justify-center text-xs font-medium text-gray-400',
                weeks: 'mt-1',
                week: 'grid grid-cols-7',
                day: 'size-9 flex items-center justify-center',
                outside: 'text-gray-300',
                disabled: 'text-gray-200 cursor-not-allowed',
              }}
              components={{
                MonthCaption: ({ calendarMonth: cm }) => (
                  <div className="flex h-9 items-center justify-center">
                    <button
                      type="button"
                      onClick={() => setShowYearMonth(true)}
                      className="hover:text-royal-500 cursor-pointer rounded-md px-2 py-1 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-50"
                    >
                      {format(cm.date, 'MMMM yyyy')}
                    </button>
                  </div>
                ),
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
